// Deal-animation overlay — plays once, right after the VS intro, as
// the cards "shuffle" at the centre and then fly to each seat. Runs
// for ~1.8s then unmounts. The real hand strip and opponent seats
// are already behind this overlay (pointer-events: none on the
// wrapper) so a slow client with animation disabled is never blocked
// from interacting.
//
// Design notes:
//   * We don't literally draw 13 cards per seat — that would be 78
//     simultaneous elements and a noticeable frame drop on weaker
//     phones. Instead we fire FIVE symbolic dealing rounds (one card
//     per seat per round, 6 seats × 5 rounds ≤ 30 elements) at a
//     staggered cadence. Each card fades out mid-flight so memory
//     stays bounded even if React is slow to unmount.
//   * Seat positions are passed in as CSS units (e.g. '6%', '50%')
//     exactly as the Seat component uses them — that way the deal
//     targets match the seat locations pixel-for-pixel.

import { useEffect, useMemo, useState } from 'react';

export type DealTarget = {
  seat:  string;
  top?:    string;
  bottom?: string;
  left?:   string;
  right?:  string;
};

type Props = {
  targets: DealTarget[];
  onDone: () => void;
};

const ROUNDS = 5;
const STAGGER_MS = 55;      // gap between each card launch
const FLIGHT_MS  = 420;     // per-card flight duration
const SHUFFLE_MS = 650;     // shuffle phase before dealing begins

type FlightCard = {
  key: string;
  targetStyle: React.CSSProperties;
  delayMs: number;
};

export default function DealAnimation({ targets, onDone }: Props) {
  const [phase, setPhase] = useState<'shuffle' | 'deal'>('shuffle');

  useEffect(() => {
    // Shuffle → Deal → Done. One-shot timers; cleared if the component
    // unmounts mid-animation (e.g. reconnect).
    const shuffleDone = setTimeout(() => setPhase('deal'), SHUFFLE_MS);
    const dealLength  = STAGGER_MS * ROUNDS * targets.length + FLIGHT_MS;
    const doneAt      = setTimeout(onDone, SHUFFLE_MS + dealLength + 120);
    return () => { clearTimeout(shuffleDone); clearTimeout(doneAt); };
  }, [targets.length, onDone]);

  // Build the per-card flight list once per (targets, phase).
  const flights = useMemo<FlightCard[]>(() => {
    if (phase !== 'deal') return [];
    const list: FlightCard[] = [];
    for (let round = 0; round < ROUNDS; round++) {
      for (let i = 0; i < targets.length; i++) {
        const t = targets[i];
        // Stagger so seats get cards in order, round by round.
        const delayMs = (round * targets.length + i) * STAGGER_MS;
        list.push({
          key: `r${round}-${t.seat}`,
          delayMs,
          targetStyle: {
            top:    t.top,
            bottom: t.bottom,
            left:   t.left,
            right:  t.right,
          },
        });
      }
    }
    return list;
  }, [phase, targets]);

  return (
    <div className="rummy-deal-overlay" aria-hidden>
      {/* Closed-deck stack that shuffles, then disappears once the
          dealing phase begins so flight cards "come from" the centre. */}
      {phase === 'shuffle' && (
        <div className="rummy-deal-stack">
          <span className="rummy-card-back rummy-deal-stack__a" />
          <span className="rummy-card-back rummy-deal-stack__b" />
          <span className="rummy-card-back rummy-deal-stack__c" />
        </div>
      )}

      {/* Flying cards — each starts at the centre and animates to its
          seat's target position with a CSS transition. The animation
          is driven by a staggered requestAnimationFrame so iOS Safari
          doesn't coalesce the per-element transforms. */}
      {flights.map((fl) => (
        <FlightCardEl key={fl.key} flight={fl} />
      ))}
    </div>
  );
}

// A single card that starts at the centre and, after `delayMs`,
// transitions to its target position. Pure CSS transition under the
// hood — we don't keyframe it because the TARGET varies per seat.
function FlightCardEl({ flight }: { flight: FlightCard }) {
  const [launched, setLaunched] = useState(false);
  useEffect(() => {
    // Two RAFs ensure the initial transform is committed before the
    // transition begins, so the card actually travels from centre → seat
    // on every phone rather than teleporting.
    const t = setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => setLaunched(true)));
    }, flight.delayMs);
    return () => clearTimeout(t);
  }, [flight.delayMs]);

  return (
    <span
      className={`rummy-deal-card ${launched ? 'rummy-deal-card--launched' : ''}`}
      style={{
        transitionDuration: `${FLIGHT_MS}ms`,
        ...(launched ? flight.targetStyle : {}),
      }}
    />
  );
}
